import { INDICATOR_TEMPLATES_STORAGE_KEY, listIndicatorTemplates, normalizeIndicatorTemplate } from './indicatorTemplates.js'

export const TEMPLATE_LIBRARY_SCOPE = 'indicator-template-library-production-v1'
const clone = value => JSON.parse(JSON.stringify(value))

// The legacy key had no owner. Bind it once, only after an authenticated server
// read succeeds. Never remove its bytes or import it into a subsequent account.
export function claimLegacyTemplates(userId, storage = globalThis.localStorage) {
  if (!userId || !storage) return []
  try {
    const key = `${INDICATOR_TEMPLATES_STORAGE_KEY}.owner`
    const owner = storage.getItem(key)
    if (owner && owner !== userId) return []
    if (!owner) storage.setItem(key, userId)
    return listIndicatorTemplates()
  } catch { return [] }
}

async function importId(template) {
  const bytes = new TextEncoder().encode(JSON.stringify(template))
  const digest = await crypto.subtle.digest('SHA-256', bytes)
  return `legacy-${Array.from(new Uint8Array(digest), n => n.toString(16).padStart(2, '0')).join('')}`
}
function preserve(...lists) {
  return [...new Map(lists.flat().filter(Boolean).map(value => [JSON.stringify(value), value])).values()]
}

export function createTemplateLibrary(transport, options = {}) {
  let records = {}, pending = {}, reading, writing, ready = false, imported = false
  let state = { templates: [], status: 'loading' }
  const listeners = new Set()
  try { pending = options.load?.() || {} } catch { /* keep server authority */ }
  function emit(status = ready ? 'ready' : 'loading') {
    const templates = Object.entries({ ...records, ...pending }).map(([id, row]) => normalizeIndicatorTemplate({ ...row.template, id }))
      .filter(Boolean).sort((a, b) => a.name.localeCompare(b.name) || a.id.localeCompare(b.id))
    if (status !== state.status || JSON.stringify(templates) !== JSON.stringify(state.templates)) {
      state = { templates, status }
      listeners.forEach(fn => fn())
    }
    try { options.persist?.(pending) } catch { /* retain memory retry */ }
  }
  async function flush() {
    if (writing) return writing
    if (!ready) return false
    writing = (async () => {
      for (const id of Object.keys(pending)) {
        while (pending[id]) {
          const draft = pending[id]
          const current = records[id]
          // A completed import is immutable. Later edits must never be reset by
          // opening another browser that still has the original local template.
          if (draft.importOnly && current) { delete pending[id]; emit(); break }
          const next = {
            version: 1, revision: crypto.randomUUID(), template: draft.template,
            originals: preserve(current?.originals || [], current?.template, draft.originals || []),
          }
          try {
            if (!await transport.write(id, next, current?.revision ?? null)) return false
            records = { ...records, [id]: next }
            if (pending[id] === draft) delete pending[id]
            emit()
          } catch { return false }
        }
      }
      return true
    })()
    try { return await writing } finally { writing = null }
  }
  function refresh() {
    if (reading) return reading
    reading = (async () => {
      try {
        // A read cannot roll back a completed write.
        if (writing) await writing
        const before = records
        const next = await transport.read()
        for (const id of Object.keys(records)) if (before[id] !== records[id]) next[id] = records[id]
        records = next
        if (!imported) {
          for (const raw of await options.legacy?.() || []) {
            const template = normalizeIndicatorTemplate(raw)
            if (!template) continue
            const id = await importId(template)
            if (!records[id] && !pending[id]) pending[id] = { template, originals: [template], importOnly: true }
          }
          imported = true
        }
        ready = true
        emit()
        await flush()
        return true
      } catch { emit(ready ? 'ready' : 'load-error'); return false } finally { reading = null }
    })()
    return reading
  }
  function save(indicator, name, source = {}) {
    const cleanName = String(name || '').trim()
    if (!cleanName || !indicator?.kind) return null
    const existing = state.templates.find(t => t.kind === indicator.kind && t.name.toLowerCase() === cleanName.toLowerCase())
    const id = existing?.id || `named-${encodeURIComponent(`${indicator.kind}:${cleanName.toLowerCase()}`)}`
    const timestamp = new Date().toISOString()
    const template = normalizeIndicatorTemplate({
      id, name: cleanName, kind: indicator.kind, params: indicator.params, style: indicator.style,
      source, createdAt: existing?.createdAt || timestamp, updatedAt: timestamp,
    })
    if (!template) return null
    pending[id] = { template, originals: preserve(pending[id]?.originals || [], pending[id]?.template) }
    emit()
    void flush()
    return clone(template)
  }
  emit()
  return { getSnapshot: () => state, subscribe: fn => { listeners.add(fn); return () => listeners.delete(fn) }, refresh, flush, save }
}
